import { FilterScope, ProductPriceType } from "@scayla/api-client-fetch";

import { $t } from "@/i18n";
import { FilterModuleKey } from "@/store/filterStore";

/**
 * Column ids. They double as the persisted `ColumnSetting.fieldSlug` of a filterset or template
 * field block, so they must stay stable — the reference columns keep their original singular slug
 * even though the API fields behind them are now the plural `*RecordIds` lists.
 */
export const PRODUCT_PRICE_COLUMNS = {
  NAME: "name",
  NET_PRICE: "netPrice",
  ADDITIONAL_COSTS: "additionalCosts",
  ADDITIONAL_COSTS_INCL_VAT: "additionalCostsInclVat",
  GROSS_PRICE: "grossPrice",
  VAT_RATE: "vatRate",
  VAT_AMOUNT: "vatAmount",
  GROSS_PRICE_INCL_COSTS: "grossPriceInclCosts",
  CURRENCY: "currency",
  EXCHANGE_RATE: "exchangeRate",
  RECORD_LINKS: "recordLinkIds",
  STATUS: "statusId",
  TERMINATION_REASONS: "terminationReasons",
  NOTE: "note",
  PRICE_INDEX: "priceIndex",
  INTEREST_RATE: "interestRate",
  LISTING_PORTALS: "listingPortals",
  ASSET: "assetRecordId",
  PROJECT: "projectRecordId",
  LISTING_START_DATE: "listingStartDate",
  RENTAL_NOTE: "rentalNote",
  VACANT_FROM: "vacantFrom",
} as const;

export type ProductPriceColumn = (typeof PRODUCT_PRICE_COLUMNS)[keyof typeof PRODUCT_PRICE_COLUMNS];

/**
 * Columns whose cells resolve record ids through the record-refs lookup instead of reading a row
 * property. They are the ones a change of that lookup affects, in the grid and in the colDefs alike.
 */
export const PRODUCT_PRICE_REFERENCE_COLUMNS: ProductPriceColumn[] = [
  PRODUCT_PRICE_COLUMNS.RECORD_LINKS,
  PRODUCT_PRICE_COLUMNS.ASSET,
  PRODUCT_PRICE_COLUMNS.PROJECT,
];

/**
 * Full column order (visible + hidden). Keep it stable — it is serialized into
 * `ColumnSetting.order`.
 */
export const DEFAULT_PRODUCT_PRICE_COLUMN_ORDER: ProductPriceColumn[] = [
  PRODUCT_PRICE_COLUMNS.NAME,
  PRODUCT_PRICE_COLUMNS.NET_PRICE,
  PRODUCT_PRICE_COLUMNS.ADDITIONAL_COSTS,
  PRODUCT_PRICE_COLUMNS.ADDITIONAL_COSTS_INCL_VAT,
  PRODUCT_PRICE_COLUMNS.GROSS_PRICE,
  PRODUCT_PRICE_COLUMNS.VAT_RATE,
  PRODUCT_PRICE_COLUMNS.VAT_AMOUNT,
  PRODUCT_PRICE_COLUMNS.GROSS_PRICE_INCL_COSTS,
  PRODUCT_PRICE_COLUMNS.CURRENCY,
  PRODUCT_PRICE_COLUMNS.EXCHANGE_RATE,
  PRODUCT_PRICE_COLUMNS.RECORD_LINKS,
  PRODUCT_PRICE_COLUMNS.STATUS,
  PRODUCT_PRICE_COLUMNS.TERMINATION_REASONS,
  PRODUCT_PRICE_COLUMNS.NOTE,
  PRODUCT_PRICE_COLUMNS.PRICE_INDEX,
  PRODUCT_PRICE_COLUMNS.INTEREST_RATE,
  PRODUCT_PRICE_COLUMNS.LISTING_PORTALS,
  PRODUCT_PRICE_COLUMNS.ASSET,
  PRODUCT_PRICE_COLUMNS.PROJECT,
  PRODUCT_PRICE_COLUMNS.LISTING_START_DATE,
  PRODUCT_PRICE_COLUMNS.RENTAL_NOTE,
  PRODUCT_PRICE_COLUMNS.VACANT_FROM,
];

/** Leading columns shared by every price type, in spec order. */
const COMMON_VISIBLE_COLUMNS: ProductPriceColumn[] = [
  PRODUCT_PRICE_COLUMNS.NAME,
  PRODUCT_PRICE_COLUMNS.NET_PRICE,
  PRODUCT_PRICE_COLUMNS.ADDITIONAL_COSTS,
  PRODUCT_PRICE_COLUMNS.ADDITIONAL_COSTS_INCL_VAT,
  PRODUCT_PRICE_COLUMNS.GROSS_PRICE,
  PRODUCT_PRICE_COLUMNS.VAT_RATE,
  PRODUCT_PRICE_COLUMNS.VAT_AMOUNT,
  PRODUCT_PRICE_COLUMNS.GROSS_PRICE_INCL_COSTS,
  PRODUCT_PRICE_COLUMNS.CURRENCY,
  PRODUCT_PRICE_COLUMNS.EXCHANGE_RATE,
  PRODUCT_PRICE_COLUMNS.RECORD_LINKS,
  PRODUCT_PRICE_COLUMNS.STATUS,
];

/** Trailing columns shared by every price type, after the type-specific ones. */
const COMMON_TRAILING_COLUMNS: ProductPriceColumn[] = [
  PRODUCT_PRICE_COLUMNS.LISTING_PORTALS,
  PRODUCT_PRICE_COLUMNS.ASSET,
  PRODUCT_PRICE_COLUMNS.PROJECT,
  PRODUCT_PRICE_COLUMNS.LISTING_START_DATE,
  PRODUCT_PRICE_COLUMNS.RENTAL_NOTE,
];

/** Sale/purchase: exchange rate shown, the rental-only ones hidden. */
const TRADE_VISIBLE_COLUMNS: ProductPriceColumn[] = [
  ...COMMON_VISIBLE_COLUMNS,
  PRODUCT_PRICE_COLUMNS.NOTE,
  ...COMMON_TRAILING_COLUMNS,
];

/** Rent/rent-out: adds termination/index/interest/vacancy on top of the common set. */
const RENTAL_VISIBLE_COLUMNS: ProductPriceColumn[] = [
  ...COMMON_VISIBLE_COLUMNS,
  PRODUCT_PRICE_COLUMNS.TERMINATION_REASONS,
  PRODUCT_PRICE_COLUMNS.NOTE,
  PRODUCT_PRICE_COLUMNS.PRICE_INDEX,
  PRODUCT_PRICE_COLUMNS.INTEREST_RATE,
  ...COMMON_TRAILING_COLUMNS,
  PRODUCT_PRICE_COLUMNS.VACANT_FROM,
];

export const DEFAULT_PRODUCT_PRICE_VISIBLE_COLUMNS: Record<ProductPriceType, ProductPriceColumn[]> = {
  [ProductPriceType.SALES]: TRADE_VISIBLE_COLUMNS,
  [ProductPriceType.PURCHASE]: TRADE_VISIBLE_COLUMNS,
  [ProductPriceType.RENT]: RENTAL_VISIBLE_COLUMNS,
  [ProductPriceType.RENT_OUT]: RENTAL_VISIBLE_COLUMNS,
};

export const PRODUCT_PRICE_COLUMN_DEFAULT_WIDTHS: Record<ProductPriceColumn, number> = {
  [PRODUCT_PRICE_COLUMNS.NAME]: 120,
  [PRODUCT_PRICE_COLUMNS.NET_PRICE]: 200,
  [PRODUCT_PRICE_COLUMNS.ADDITIONAL_COSTS]: 200,
  [PRODUCT_PRICE_COLUMNS.ADDITIONAL_COSTS_INCL_VAT]: 230,
  [PRODUCT_PRICE_COLUMNS.GROSS_PRICE]: 230,
  [PRODUCT_PRICE_COLUMNS.VAT_RATE]: 120,
  [PRODUCT_PRICE_COLUMNS.VAT_AMOUNT]: 120,
  [PRODUCT_PRICE_COLUMNS.GROSS_PRICE_INCL_COSTS]: 230,
  [PRODUCT_PRICE_COLUMNS.CURRENCY]: 120,
  [PRODUCT_PRICE_COLUMNS.EXCHANGE_RATE]: 120,
  [PRODUCT_PRICE_COLUMNS.RECORD_LINKS]: 200,
  [PRODUCT_PRICE_COLUMNS.STATUS]: 120,
  [PRODUCT_PRICE_COLUMNS.TERMINATION_REASONS]: 200,
  [PRODUCT_PRICE_COLUMNS.NOTE]: 200,
  [PRODUCT_PRICE_COLUMNS.PRICE_INDEX]: 120,
  [PRODUCT_PRICE_COLUMNS.INTEREST_RATE]: 160,
  [PRODUCT_PRICE_COLUMNS.LISTING_PORTALS]: 200,
  [PRODUCT_PRICE_COLUMNS.ASSET]: 160,
  [PRODUCT_PRICE_COLUMNS.PROJECT]: 160,
  [PRODUCT_PRICE_COLUMNS.LISTING_START_DATE]: 160,
  [PRODUCT_PRICE_COLUMNS.RENTAL_NOTE]: 200,
  [PRODUCT_PRICE_COLUMNS.VACANT_FROM]: 200,
};

/**
 * Column header i18n key, resolved per price type. A plain string is used when every price type
 * shares the same label; an object gives each type its own key. The `Record<ProductPriceType, …>`
 * form forces a key to be declared for all four types, so none is ever left implicit.
 *
 * Price type ↔ spec column: SALES = Verkauf, PURCHASE = Ankauf, RENT = Anmietung, RENT_OUT = Vermietung.
 */
type ColumnTitleKey = string | Record<ProductPriceType, string>;

const PRODUCT_PRICE_COLUMN_TITLE_KEYS: Record<ProductPriceColumn, ColumnTitleKey> = {
  [PRODUCT_PRICE_COLUMNS.NAME]: "common.label.name",
  [PRODUCT_PRICE_COLUMNS.NET_PRICE]: {
    [ProductPriceType.SALES]: "record.productPrice.netPricePurchase",
    [ProductPriceType.PURCHASE]: "record.productPrice.netPricePurchase",
    [ProductPriceType.RENT]: "record.productPrice.netPriceRental",
    [ProductPriceType.RENT_OUT]: "record.productPrice.netPriceRental",
  },
  [PRODUCT_PRICE_COLUMNS.ADDITIONAL_COSTS]: {
    [ProductPriceType.SALES]: "record.productPrice.additionalCostsPurchase",
    [ProductPriceType.PURCHASE]: "record.productPrice.additionalCostsPurchase",
    [ProductPriceType.RENT]: "record.productPrice.additionalCostsRental",
    [ProductPriceType.RENT_OUT]: "record.productPrice.additionalCostsRental",
  },
  [PRODUCT_PRICE_COLUMNS.ADDITIONAL_COSTS_INCL_VAT]: {
    [ProductPriceType.SALES]: "record.productPrice.additionalCostsInclVatPurchase",
    [ProductPriceType.PURCHASE]: "record.productPrice.additionalCostsInclVatPurchase",
    [ProductPriceType.RENT]: "record.productPrice.additionalCostsInclVatRental",
    [ProductPriceType.RENT_OUT]: "record.productPrice.additionalCostsInclVatRental",
  },
  [PRODUCT_PRICE_COLUMNS.GROSS_PRICE]: {
    [ProductPriceType.SALES]: "record.productPrice.grossExclVatPurchase",
    [ProductPriceType.PURCHASE]: "record.productPrice.grossExclVatPurchase",
    [ProductPriceType.RENT]: "record.productPrice.grossExclVatRental",
    [ProductPriceType.RENT_OUT]: "record.productPrice.grossExclVatRental",
  },
  [PRODUCT_PRICE_COLUMNS.VAT_RATE]: "record.productPrice.vatRate",
  [PRODUCT_PRICE_COLUMNS.VAT_AMOUNT]: "record.productPrice.vatAmount",
  [PRODUCT_PRICE_COLUMNS.GROSS_PRICE_INCL_COSTS]: {
    [ProductPriceType.SALES]: "record.productPrice.grossInclVatPurchase",
    [ProductPriceType.PURCHASE]: "record.productPrice.grossInclVatPurchase",
    [ProductPriceType.RENT]: "record.productPrice.grossInclVatRental",
    [ProductPriceType.RENT_OUT]: "record.productPrice.grossInclVatRental",
  },
  [PRODUCT_PRICE_COLUMNS.CURRENCY]: "record.productPrice.currency",
  [PRODUCT_PRICE_COLUMNS.EXCHANGE_RATE]: "record.productPrice.exchangeRate",
  [PRODUCT_PRICE_COLUMNS.RECORD_LINKS]: {
    [ProductPriceType.SALES]: "record.productPrice.customer",
    [ProductPriceType.PURCHASE]: "record.productPrice.customer",
    [ProductPriceType.RENT]: "record.productPrice.renter",
    [ProductPriceType.RENT_OUT]: "record.productPrice.renter",
  },
  [PRODUCT_PRICE_COLUMNS.STATUS]: "common.label.status",
  [PRODUCT_PRICE_COLUMNS.TERMINATION_REASONS]: "record.productPrice.terminationReason",
  [PRODUCT_PRICE_COLUMNS.NOTE]: "record.productPrice.note",
  [PRODUCT_PRICE_COLUMNS.PRICE_INDEX]: "record.productPrice.priceIndex",
  [PRODUCT_PRICE_COLUMNS.INTEREST_RATE]: "record.productPrice.interestRate",
  [PRODUCT_PRICE_COLUMNS.LISTING_PORTALS]: {
    [ProductPriceType.SALES]: "record.productPrice.classifiedsWebsites",
    [ProductPriceType.PURCHASE]: "record.productPrice.classifiedsWebsites",
    [ProductPriceType.RENT]: "record.productPrice.propertyListingWebsites",
    [ProductPriceType.RENT_OUT]: "record.productPrice.propertyListingWebsites",
  },
  [PRODUCT_PRICE_COLUMNS.ASSET]: "common.label.asset",
  [PRODUCT_PRICE_COLUMNS.PROJECT]: "common.label.project",
  [PRODUCT_PRICE_COLUMNS.LISTING_START_DATE]: "record.productPrice.advertStart",
  [PRODUCT_PRICE_COLUMNS.RENTAL_NOTE]: {
    [ProductPriceType.SALES]: "record.productPrice.notePurchase",
    [ProductPriceType.PURCHASE]: "record.productPrice.notePurchase",
    [ProductPriceType.RENT]: "record.productPrice.rentalNote",
    [ProductPriceType.RENT_OUT]: "record.productPrice.rentalNote",
  },
  [PRODUCT_PRICE_COLUMNS.VACANT_FROM]: "record.productPrice.vacancyFrom",
};

export function getProductPriceColumnTitle(column: ProductPriceColumn, priceType: ProductPriceType): string {
  const entry = PRODUCT_PRICE_COLUMN_TITLE_KEYS[column];
  const key = typeof entry === "string" ? entry : entry[priceType];
  return $t(key);
}

/**
 * One filterset scope per price tab: a Sales filterset must never show up under Purchase, Rent or
 * Rent out. Scope is the only context the filter list is narrowed by, so separate values are what
 * keeps the tabs isolated.
 */
/** Heading of the collapsible history section, named after the price type. */
export function getProductPriceOldSectionLabel(priceType: ProductPriceType): string {
  switch (priceType) {
    case ProductPriceType.SALES:
      return $t("record.productPrice.oldPriceSale");
    case ProductPriceType.PURCHASE:
      return $t("record.productPrice.oldPricePurchase");
    case ProductPriceType.RENT:
      return $t("record.productPrice.oldPriceRent");
    case ProductPriceType.RENT_OUT:
      return $t("record.productPrice.oldPriceRentOut");
    default:
      return "";
  }
}

export const PRODUCT_PRICE_FILTER_SCOPE_BY_TYPE: Record<ProductPriceType, FilterScope> = {
  [ProductPriceType.SALES]: FilterScope.PRODUCT_PRICE_SALE,
  [ProductPriceType.PURCHASE]: FilterScope.PRODUCT_PRICE_PURCHASE,
  [ProductPriceType.RENT]: FilterScope.PRODUCT_PRICE_RENT,
  [ProductPriceType.RENT_OUT]: FilterScope.PRODUCT_PRICE_RENTOUT,
};

/** Every price scope, for the places that special-case "this is not a record-module filterset". */
export const PRODUCT_PRICE_FILTER_SCOPES: FilterScope[] = Object.values(PRODUCT_PRICE_FILTER_SCOPE_BY_TYPE);

/** Filter-row visibility is remembered per price tab, so each tab gets its own store key. */
export const PRODUCT_PRICE_FILTER_MODULE_KEY_BY_TYPE: Record<ProductPriceType, FilterModuleKey> = {
  [ProductPriceType.SALES]: FilterModuleKey.PRODUCT_PRICE_SALE,
  [ProductPriceType.PURCHASE]: FilterModuleKey.PRODUCT_PRICE_PURCHASE,
  [ProductPriceType.RENT]: FilterModuleKey.PRODUCT_PRICE_RENT,
  [ProductPriceType.RENT_OUT]: FilterModuleKey.PRODUCT_PRICE_RENT_OUT,
};

/** Fixed list until currency management exists; then sourced from there. */
export const PRODUCT_PRICE_CURRENCIES = ["CHF", "EUR"] as const;

export const DEFAULT_PRODUCT_PRICE_CURRENCY = "CHF";

export function getDefaultProductPriceColumnSettings(priceType: ProductPriceType) {
  const visible = new Set(DEFAULT_PRODUCT_PRICE_VISIBLE_COLUMNS[priceType]);
  return DEFAULT_PRODUCT_PRICE_COLUMN_ORDER.map((fieldSlug, idx) => ({
    fieldSlug,
    isVisible: visible.has(fieldSlug),
    width: PRODUCT_PRICE_COLUMN_DEFAULT_WIDTHS[fieldSlug],
    order: idx,
  }));
}
